import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface ReferralState {
  code: string | null;
  referrerId: string | null;
  capturedAt: number | null;
}

interface ReferralContextType {
  state: ReferralState;
  clearReferral: () => void;
  hasActiveReferral: boolean;
}

const REFERRAL_KEY = 'vision_referral';
const EXPIRY_DAYS = 7;

const ReferralContext = createContext<ReferralContextType | null>(null);

export const ReferralProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ReferralState>(() => {
    try {
      const stored = localStorage.getItem(REFERRAL_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const expiry = parsed.capturedAt + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        if (Date.now() < expiry) return parsed;
        localStorage.removeItem(REFERRAL_KEY);
      }
    } catch {}
    return { code: null, referrerId: null, capturedAt: null };
  });

  useEffect(() => {
    const code = searchParams.get('ref') || searchParams.get('referral') || searchParams.get('affiliate');
    const uid = searchParams.get('uid');

    if (code) {
      const newState: ReferralState = { code, referrerId: uid, capturedAt: Date.now() };
      setState(newState);
      localStorage.setItem(REFERRAL_KEY, JSON.stringify(newState));
    }
  }, [searchParams]);

  const clearReferral = useCallback(() => {
    setState({ code: null, referrerId: null, capturedAt: null });
    localStorage.removeItem(REFERRAL_KEY);
  }, []);

  const hasActiveReferral = !!state.code && !!state.capturedAt &&
    Date.now() < state.capturedAt + EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  return (
    <ReferralContext.Provider value={{ state, clearReferral, hasActiveReferral }}>
      {children}
    </ReferralContext.Provider>
  );
};

export const useReferral = () => {
  const context = useContext(ReferralContext);
  if (!context) throw new Error('useReferral must be used within ReferralProvider');
  return context;
};
